import type { Metadata, MetadataRoute } from "@/lib/page-metadata";
import { brandNames } from "@/lib/brand";
import {
  ATLAS_PORTAL_HOSTNAME,
  ATLAS_PORTAL_ORIGIN,
  normalizePortalOrigin,
} from "@/lib/portal-domain";

export const SITE_NAME = brandNames.portal;
export const SITE_SHORT_NAME = brandNames.portalShort;
export const SITE_ORIGIN =
  normalizePortalOrigin(process.env.NEXT_PUBLIC_SITE_URL) ||
  ATLAS_PORTAL_ORIGIN;
export const SITE_HOSTNAME =
  normalizePortalOrigin(SITE_ORIGIN) === ATLAS_PORTAL_ORIGIN
    ? ATLAS_PORTAL_HOSTNAME
    : new URL(SITE_ORIGIN).hostname;

export const DEFAULT_SITE_DESCRIPTION = `${brandNames.portal} delivers subscriber research across the full AI stack, including ${brandNames.publication} newsletter, interactive diligence, and source-linked AI economy intelligence.`;

// Direct Substack checkout where prospects start a subscription. The portal
// (this app) handles login; new-subscriber purchase stays on the publication.
export const SUBSTACK_SUBSCRIBE_URL =
  "https://www.thediligencestack.com/subscribe";
export const SUBSCRIBE_URL = "/subscribe";

export function buildSubscribeUrl(email?: string) {
  const normalizedEmail = email?.trim().toLowerCase();
  return normalizedEmail
    ? `${SUBSCRIBE_URL}?email=${encodeURIComponent(normalizedEmail)}`
    : SUBSCRIBE_URL;
}

export const SITE_LAST_MODIFIED = "2026-07-28T00:00:00.000Z";

const SOCIAL_IMAGE = {
  url: "/opengraph-image",
  width: 1200,
  height: 630,
  alt: brandNames.portal,
};

const indexableRobots = {
  index: true,
  follow: true,
  googleBot: {
    index: true,
    follow: true,
    "max-image-preview": "large",
    "max-snippet": -1,
    "max-video-preview": -1,
  },
} satisfies Metadata["robots"];

const privateRobots = {
  index: false,
  follow: false,
  googleBot: {
    index: false,
    follow: false,
    noimageindex: true,
  },
} satisfies Metadata["robots"];

type SiteRoute = {
  changeFrequency: MetadataRoute.Sitemap[number]["changeFrequency"];
  description: string;
  indexable: boolean;
  path: string;
  priority: number;
  /**
   * Open Graph / Twitter title. Defaults to `title`. Keep this short enough
   * for iMessage and Slack unfurls — do not append the site name; `og:site_name`
   * already carries it.
   */
  socialTitle?: string;
  title: string;
};

export const siteRoutes = {
  home: {
    changeFrequency: "weekly",
    description: DEFAULT_SITE_DESCRIPTION,
    indexable: true,
    path: "/",
    priority: 1,
    socialTitle: SITE_NAME,
    title: "AI Economy Intelligence",
  },
  dashboard: {
    changeFrequency: "weekly",
    description: "Subscriber dashboard and product launcher.",
    indexable: false,
    path: "/dashboard",
    priority: 0.4,
    title: "Dashboard",
  },
  login: {
    changeFrequency: "monthly",
    description: `Sign in to ${brandNames.portal} for source-linked AI economy research across infrastructure, silicon, software, markets, and the full AI stack.`,
    indexable: true,
    path: "/login",
    priority: 1,
    title: "AI Economy Portal Access",
  },
  subscribe: {
    changeFrequency: "monthly",
    description:
      "Compare Creative Strategies research subscriptions, direct research-desk access, financial models, advisory, and enterprise plans.",
    indexable: true,
    path: "/subscribe",
    priority: 0.9,
    title: "Research Subscriptions",
  },
  security: {
    changeFrequency: "yearly",
    description: "Atlas application security controls, enterprise integration status and procurement evidence.",
    indexable: true,
    path: "/security",
    priority: 0.3,
    title: "Security",
  },
  privacy: {
    changeFrequency: "yearly",
    description: `How ${brandNames.publisher} collects, uses, and protects subscriber information across ${brandNames.portal}, connected AI assistants, and the iOS app.`,
    indexable: true,
    path: "/privacy",
    priority: 0.3,
    title: "Privacy Policy",
  },
  terms: {
    changeFrequency: "yearly",
    description: `The subscriber terms that govern access to ${brandNames.portal}, the research agent, connected AI assistants, and the iOS app.`,
    indexable: true,
    path: "/terms",
    priority: 0.3,
    title: "Terms of Service",
  },
  support: {
    changeFrequency: "monthly",
    description: `Get help with ${brandNames.portalShort} sign-in, ${brandNames.publication} subscriptions, the research agent, connected apps, and privacy requests.`,
    indexable: true,
    path: "/support",
    priority: 0.5,
    title: "Support",
  },
  portalBilling: {
    changeFrequency: "monthly",
    description:
      "Plan, renewal, and payment details for a CS Atlas subscription.",
    indexable: false,
    path: "/dashboard/billing",
    priority: 0.1,
    title: "Billing",
  },
  portalSupport: {
    changeFrequency: "monthly",
    description:
      "Subscriber support for CS Atlas accounts, subscriptions, research tools, and connected apps.",
    indexable: false,
    path: "/dashboard/support",
    priority: 0.1,
    title: "Support",
  },
  dataCenterDiligence: {
    changeFrequency: "weekly",
    description:
      "Source-linked AI data-center diligence with capacity signals, risk scores, facility maps, and unresolved questions across the U.S. footprint.",
    indexable: false,
    path: "/data-center-diligence",
    priority: 0.8,
    title: "Data Center Diligence",
  },
  ctoStudy: {
    changeFrequency: "monthly",
    description:
      "Enterprise AI research with question-level data on adoption, budgets, and operating priorities.",
    indexable: false,
    path: "/cto-study",
    priority: 0.7,
    title: "Enterprise AI Index",
  },
  pcMarketTam: {
    changeFrequency: "monthly",
    description:
      "PC shipment, revenue, silicon TAM, AI-PC share, and scenario sensitivity models for the hardware cycle.",
    indexable: false,
    path: "/pc-market-tam",
    priority: 0.7,
    title: "PC Market TAM",
  },
  legacyFinancialModels: {
    changeFrequency: "weekly",
    description:
      "Legacy subscriber models with source-linked charts, structured datasets, methodology, and downloadable artifacts.",
    indexable: false,
    path: "/legacy-models",
    priority: 0.8,
    title: "Legacy Financial Models",
  },
  financialModels: {
    changeFrequency: "weekly",
    description:
      "Excel-backed subscriber data models with research dashboards, immutable revision history, and controlled downloads.",
    indexable: false,
    path: "/models",
    priority: 0.8,
    title: "Data Models",
  },
  financialScorecard: {
    changeFrequency: "weekly",
    description:
      "Fundamental scorecard for companies tracked across the AI economy.",
    indexable: false,
    path: "/financial-scorecard",
    priority: 0.7,
    title: "Financial Scorecard",
  },
  financialDashboard: {
    changeFrequency: "weekly",
    description:
      "Bloomberg-style company terminal with SEC financials, filings, and research hooks.",
    indexable: false,
    path: "/financial-dashboard",
    priority: 0.7,
    title: "Financial Dashboard",
  },
  capacityRiskTracker: {
    changeFrequency: "weekly",
    description:
      "Company and category evidence on pricing leverage, contract duration, and capacity-risk transfer.",
    indexable: false,
    path: "/capacity-risk-transfer",
    priority: 0.7,
    title: "Capacity Risk Transfer",
  },
  financialDashboardCompany: {
    changeFrequency: "weekly",
    description:
      "Company terminal with SEC financials, filings, and research hooks for one tracked ticker.",
    indexable: false,
    path: "/financial-dashboard/[ticker]",
    priority: 0.6,
    title: "Financial Dashboard Company",
  },
  siliconSystemsEra: {
    changeFrequency: "monthly",
    description:
      "Semi forecast with source-normalized device revenue, Creative Strategies systems-era detail, WFE, foundry, packaging, AI rack, and CoWoS signposts.",
    indexable: false,
    path: "/silicon-systems-era",
    priority: 0.7,
    title: "Semi Forecast",
  },
  aiInfrastructureBacklog: {
    changeFrequency: "weekly",
    description:
      "Monetizable MW dashboard comparing powered-shell landlords, GPU clouds, neoclouds, AI infrastructure hosts, and hyperscaler capacity.",
    indexable: false,
    path: "/ai-infrastructure-backlog",
    priority: 0.7,
    title: "AI Infrastructure Backlog",
  },
  tokenomicsModel: {
    changeFrequency: "monthly",
    description:
      "Quantitative frameworks for compute-token relationships, staking economics, and demand-curve modeling.",
    indexable: false,
    path: "/tokenomics-model",
    priority: 0.3,
    title: "Tokenomics Model",
  },
  agent: {
    changeFrequency: "weekly",
    description:
      "Ask Atlas Agent for grounded answers with citations from the Creative Strategies research corpus.",
    indexable: false,
    path: "/agent",
    priority: 0.6,
    title: brandNames.agent,
  },
  notes: {
    changeFrequency: "daily",
    description:
      "Published research notes and market commentary from the Creative Strategies research team.",
    indexable: false,
    path: "/notes",
    priority: 0.6,
    title: "Notes",
  },
  admin: {
    changeFrequency: "yearly",
    description:
      "Navigate CS Atlas administration by access, AI, content, financial research, integrations, and system health.",
    indexable: false,
    path: "/admin",
    priority: 0.1,
    title: "Admin Overview",
  },
  advancedAgent: {
    changeFrequency: "yearly",
    description:
      "Atlas Agent Pro combines Creative Strategies research with web search, analysis, and files for complex research tasks.",
    indexable: false,
    path: "/advanced-agent",
    priority: 0.1,
    title: brandNames.agentPro,
  },
  adminUsers: {
    changeFrequency: "yearly",
    description:
      "Manage CS Atlas members, companies, billing, and reusable access policies.",
    indexable: false,
    path: "/admin/users",
    priority: 0.1,
    title: "Access Management",
  },
  adminSubstackSync: {
    changeFrequency: "yearly",
    description: `Configure ${brandNames.publication} newsletter subscriber sync, credentials, and manual CSV fallback.`,
    indexable: false,
    path: "/admin/substack-sync",
    priority: 0.1,
    title: "Substack Sync",
  },
  adminPages: {
    changeFrequency: "yearly",
    description: "Manage CS Atlas page availability and hidden surfaces.",
    indexable: false,
    path: "/admin/pages",
    priority: 0.1,
    title: "Manage Pages",
  },
  adminSupport: {
    changeFrequency: "yearly",
    description:
      "Manage the CS Atlas sales pipeline, Stripe invoices, support tickets, internal comments, replies, and notifications.",
    indexable: false,
    path: "/admin/support",
    priority: 0.1,
    title: "Sales & Support",
  },
  adminNotes: {
    changeFrequency: "daily",
    description: `Validate published Ghost notes against ${brandNames.publication}'s canonical taxonomy.`,
    indexable: false,
    path: "/admin/notes",
    priority: 0.1,
    title: "Notes Taxonomy",
  },
  adminLegacyModels: {
    changeFrequency: "yearly",
    description:
      "Manage legacy financial model ingestion, review, publishing, downloads, and agent access.",
    indexable: false,
    path: "/admin/legacy-models",
    priority: 0.1,
    title: "Legacy Financial Models",
  },
  adminFinancialModels: {
    changeFrequency: "daily",
    description:
      "Manage Excel-backed data model revisions, publication, and model-specific access.",
    indexable: false,
    path: "/admin/models",
    priority: 0.1,
    title: "Data Models",
  },
  adminAgentSettings: {
    changeFrequency: "yearly",
    description:
      "Configure Creative Strategies research agent models, defaults, tools, and research libraries.",
    indexable: false,
    path: "/admin/agent-settings",
    priority: 0.1,
    title: "Agent Configuration",
  },
  adminScorecard: {
    changeFrequency: "yearly",
    description:
      "Manage the companies tracked on the Financial Scorecard by ticker.",
    indexable: false,
    path: "/admin/scorecard",
    priority: 0.1,
    title: "Financial Scorecard",
  },
  adminCapacityRiskTracker: {
    changeFrequency: "weekly",
    description:
      "Operate Capacity Risk Transfer companies, segments, research runs, reviews, and immutable publication snapshots.",
    indexable: false,
    path: "/admin/capacity-risk-transfer",
    priority: 0.1,
    title: "Capacity Risk Transfer",
  },
  adminFinancialDashboard: {
    changeFrequency: "yearly",
    description:
      "Manage the companies shown on the subscriber company terminal by ticker.",
    indexable: false,
    path: "/admin/financial-dashboard",
    priority: 0.1,
    title: "Company Terminal",
  },
  adminKnowledgeBase: {
    changeFrequency: "yearly",
    description:
      "Manage CS Atlas knowledge bases, uploads, categories, and search previews.",
    indexable: false,
    path: "/admin/knowledge-base",
    priority: 0.1,
    title: "Knowledge Base",
  },
  adminMcp: {
    changeFrequency: "yearly",
    description:
      "Manage CS Atlas MCP knowledge-base access, role defaults, member policies, and OAuth connections.",
    indexable: false,
    path: "/admin/mcp",
    priority: 0.1,
    title: "MCP & OAuth",
  },
  adminApiKeys: {
    changeFrequency: "yearly",
    description:
      "Generate and revoke Research API bearer keys for third-party knowledge-base access.",
    indexable: false,
    path: "/admin/api-keys",
    priority: 0.1,
    title: "Research API Keys",
  },
  adminOperations: {
    changeFrequency: "yearly",
    description:
      "Monitor CS Atlas admin jobs, failures, leases, and operational readiness.",
    indexable: false,
    path: "/admin/operations",
    priority: 0.1,
    title: "System Health",
  },
  adminAiOperations: {
    changeFrequency: "yearly",
    description:
      "Legacy entry point for CS Atlas AI activity and evaluation workspaces.",
    indexable: false,
    path: "/admin/ai-operations",
    priority: 0.1,
    title: "AI Administration",
  },
  adminStats: {
    changeFrequency: "yearly",
    description:
      "Creative Strategies research agent usage, token, and request metrics.",
    indexable: false,
    path: "/admin/stats",
    priority: 0.1,
    title: "Agent Stats",
  },
  adminLogs: {
    changeFrequency: "yearly",
    description:
      "Review CS Atlas AI usage, estimated cost, request logs, captured responses, and provider errors.",
    indexable: false,
    path: "/admin/logs",
    priority: 0.1,
    title: "AI Activity",
  },
  devLogs: {
    changeFrequency: "yearly",
    description:
      "CS Atlas AI model request logs and captured responses (developer view).",
    indexable: false,
    path: "/dev/logs",
    priority: 0.1,
    title: "Developer Logs",
  },
  devStats: {
    changeFrequency: "yearly",
    description:
      "Creative Strategies research agent usage, token, and request metrics (developer view).",
    indexable: false,
    path: "/dev/stats",
    priority: 0.1,
    title: "Developer Stats",
  },
  adminAgentEval: {
    changeFrequency: "yearly",
    description:
      "Grade stored agent responses and compare models with controlled prompt replays.",
    indexable: false,
    path: "/admin/agent-eval",
    priority: 0.1,
    title: "Evaluations & Replay",
  },
} satisfies Record<string, SiteRoute>;

export type SiteRouteKey = keyof typeof siteRoutes;

export function absoluteSiteUrl(path = "/") {
  return new URL(path, SITE_ORIGIN).toString();
}

export function socialCardTitle(routeKey: SiteRouteKey) {
  const route: SiteRoute = siteRoutes[routeKey];
  return route.socialTitle ?? route.title;
}

export function buildRootMetadata(): Metadata {
  return {
    applicationName: SITE_NAME,
    authors: [{ name: "Creative Strategies" }],
    creator: "Creative Strategies",
    description: DEFAULT_SITE_DESCRIPTION,
    formatDetection: {
      email: false,
      address: false,
      telephone: false,
    },
    icons: {
      icon: [
        // SVG first so browsers that support it prefer the theme-adaptive mark
        // (light/dark plate via prefers-color-scheme); raster entries are the
        // fallback for older browsers, PWA install, and search results.
        { url: "/icon.svg", type: "image/svg+xml" },
        { url: "/favicon.ico", sizes: "any" },
        { url: "/icon.png", sizes: "512x512", type: "image/png" },
      ],
      apple: [{ url: "/apple-icon.png", sizes: "180x180", type: "image/png" }],
    },
    manifest: "/manifest.webmanifest",
    metadataBase: SITE_ORIGIN,
    openGraph: {
      description: DEFAULT_SITE_DESCRIPTION,
      images: [SOCIAL_IMAGE],
      locale: "en_US",
      siteName: SITE_NAME,
      title: SITE_NAME,
      type: "website",
      url: "/",
    },
    publisher: "Creative Strategies",
    title: {
      default: SITE_NAME,
      template: `%s | ${SITE_NAME}`,
    },
    twitter: {
      card: "summary_large_image",
      description: DEFAULT_SITE_DESCRIPTION,
      images: [SOCIAL_IMAGE],
      title: SITE_NAME,
    },
  };
}

export function buildPageMetadata(routeKey: SiteRouteKey): Metadata {
  const route = siteRoutes[routeKey];
  const title = socialCardTitle(routeKey);
  const image =
    routeKey === "notes" || routeKey === "financialModels"
      ? {
          ...SOCIAL_IMAGE,
          url: routeKey === "notes" ? "/social/notes" : "/social/models",
          alt: title,
        }
      : SOCIAL_IMAGE;

  return {
    alternates: {
      canonical: route.path,
    },
    description: route.description,
    openGraph: {
      description: route.description,
      images: [image],
      locale: "en_US",
      siteName: SITE_NAME,
      title,
      type: "website",
      url: route.path,
    },
    robots: route.indexable ? indexableRobots : privateRobots,
    title: route.title,
    twitter: {
      card: "summary_large_image",
      description: route.description,
      images: [image],
      title,
    },
  };
}

export const sitemapRoutes = Object.values(siteRoutes).filter(
  (route) => route.indexable,
);
